/**
 * Hair Matters — Judge.me Cards Carousel: viewport deferral + image resize
 *
 * 1) Wraps jdgmCarouselUtils.initCarousel so cards carousels below the fold
 *    do not call reviews_for_carousel until near viewport (skeleton meantime).
 * 2) Rewrites review image URLs width=1024 → 2× of CSS card width (measured 281px → 562).
 *
 * Scoped by .jdgm-cards-carousel / carousel_type === 'cards' — not by template name.
 */
(function () {
  'use strict';

  var LOG = '[hm-jdgm]';
  var ROOT_MARGIN_PX = 400;
  var FALLBACK_CARD_CSS_PX = 281;
  var DESIGN_MODE =
    (window.Shopify && Shopify.designMode) ||
    document.documentElement.classList.contains('shopify-design-mode');

  var wrapperInstalledAt = 0;
  var deferred = Object.create(null);
  var fetchPatched = false;
  var stylesInjected = false;

  function log() {
    if (typeof console !== 'undefined' && console.log) {
      console.log.apply(console, [LOG].concat([].slice.call(arguments)));
    }
  }

  function warn() {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn.apply(console, [LOG].concat([].slice.call(arguments)));
    }
  }

  function nowMs() {
    return Math.round(
      (window.performance && performance.now && performance.now()) || Date.now()
    );
  }

  function getDocumentOffsetTop(el) {
    var top = 0;
    var node = el;
    while (node) {
      top += node.offsetTop || 0;
      node = node.offsetParent;
    }
    return top;
  }

  /**
   * Near-viewport check. Fail closed (treat as far) when layout isn't ready —
   * early getBoundingClientRect() on display:none / unlaid-out nodes is unreliable.
   * Prefer the shopify-section wrapper when the carousel itself has no box.
   */
  function isNearViewport(el, margin) {
    if (!el || typeof el.getBoundingClientRect !== 'function') return false;
    margin = typeof margin === 'number' ? margin : ROOT_MARGIN_PX;
    var measureEl = el;
    var rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      measureEl = el.closest('.shopify-section') || el.closest('.page-width') || el;
      rect = measureEl.getBoundingClientRect();
    }
    var scrollY = window.scrollY || window.pageYOffset || 0;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    var absoluteTop;
    var absoluteBottom;

    if (rect.width === 0 && rect.height === 0) {
      absoluteTop = getDocumentOffsetTop(measureEl);
      if (!absoluteTop) return false;
      absoluteBottom = absoluteTop;
    } else {
      absoluteTop = rect.top + scrollY;
      absoluteBottom = rect.bottom + scrollY;
    }

    var viewTop = scrollY - margin;
    var viewBottom = scrollY + vh + margin;
    return absoluteTop < viewBottom && absoluteBottom > viewTop;
  }

  function findCarouselEl(blockId) {
    if (!blockId) return document.querySelector('.jdgm-cards-carousel');
    var sel =
      '.jdgm-cards-carousel[data-jdgm-block-id="' +
      blockId.replace(/"/g, '\\"') +
      '"]';
    return (
      document.querySelector(sel) ||
      document.querySelector('[data-jdgm-block-id="' + blockId.replace(/"/g, '\\"') + '"]')
    );
  }

  /**
   * Retina target = 2 × computed/CSS card width.
   * Prefer Judge.me's --width-xl / --width-lg (281px on desktop), else measure, else fallback.
   */
  function getRetinaImageWidth(carouselEl) {
    var cssPx = 0;
    if (carouselEl && carouselEl.style) {
      cssPx =
        parseFloat(carouselEl.style.getPropertyValue('--width-xl')) ||
        parseFloat(carouselEl.style.getPropertyValue('--width-lg')) ||
        0;
    }
    if (!cssPx && carouselEl) {
      var img = carouselEl.querySelector('.jdgm-card img, img');
      var card = carouselEl.querySelector('.jdgm-card:not(.jdgm-clone-before):not(.jdgm-clone-after)');
      var node = img || card;
      if (node) {
        cssPx = Math.round(node.getBoundingClientRect().width) || parseFloat(getComputedStyle(node).width) || 0;
      }
    }
    if (!cssPx || cssPx < 50) cssPx = FALLBACK_CARD_CSS_PX;
    return Math.ceil(cssPx * 2);
  }

  function rewriteImageUrl(url, width) {
    if (!url || typeof url !== 'string') return url;
    if (url.indexOf('review-images.judgeme.com') === -1 && url.indexOf('judge.me') === -1) {
      return url;
    }
    if (/([?&])width=1024\b/.test(url)) {
      return url.replace(/([?&])width=1024\b/, '$1width=' + width);
    }
    if (/([?&])width=\d+/.test(url)) {
      return url.replace(/([?&])width=\d+/, '$1width=' + width);
    }
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function rewriteDeep(value, width) {
    if (Array.isArray(value)) {
      for (var i = 0; i < value.length; i++) value[i] = rewriteDeep(value[i], width);
      return value;
    }
    if (value && typeof value === 'object') {
      for (var k in value) {
        if (!Object.prototype.hasOwnProperty.call(value, k)) continue;
        var v = value[k];
        if (typeof v === 'string' && (/review-images\.judgeme\.com|\.jpe?g|\.png|\.webp/i.test(v))) {
          value[k] = rewriteImageUrl(v, width);
        } else {
          value[k] = rewriteDeep(v, width);
        }
      }
      return value;
    }
    if (typeof value === 'string') return rewriteImageUrl(value, width);
    return value;
  }

  function injectSkeletonStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    var style = document.createElement('style');
    style.id = 'hm-jdgm-carousel-defer-css';
    style.textContent =
      /* Judge.me starts cards carousels as .jdgm-hidden (display:none) until
         reviews load — override while skeleton shows so layout + IO work. */
      '.jdgm-cards-carousel.hm-jdgm-skeleton.jdgm-hidden{' +
      'display:block!important;visibility:visible!important}' +
      '.hm-jdgm-skeleton .jdgm-content{min-height:420px;position:relative}' +
      '.hm-jdgm-skeleton-row{display:flex;gap:12px;overflow:hidden;padding:8px 0 24px;' +
      'min-height:320px;box-sizing:border-box}' +
      '.hm-jdgm-skeleton-card{flex:0 0 281px;max-width:calc(100% - 24px);height:320px;' +
      'border-radius:8px;background:linear-gradient(90deg,#eee 25%,#f5f5f5 37%,#eee 63%);' +
      'background-size:400% 100%;animation:hm-jdgm-shimmer 1.4s ease infinite}' +
      '@keyframes hm-jdgm-shimmer{0%{background-position:100% 0}100%{background-position:0 0}}' +
      '@media (max-width:749px){.hm-jdgm-skeleton-card{flex-basis:200px;height:280px}' +
      '.hm-jdgm-skeleton .jdgm-content{min-height:360px}}';
    (document.head || document.documentElement).appendChild(style);
  }

  function showSkeleton(el) {
    if (!el || el.querySelector('.hm-jdgm-skeleton-row')) return;
    injectSkeletonStyles();
    el.classList.add('hm-jdgm-skeleton');
    if (el.classList.contains('jdgm-hidden')) {
      el.setAttribute('data-hm-had-jdgm-hidden', '1');
    }
    var content = el.querySelector('.jdgm-content');
    if (!content) return;
    var row = document.createElement('div');
    row.className = 'hm-jdgm-skeleton-row';
    row.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < 3; i++) {
      var card = document.createElement('div');
      card.className = 'hm-jdgm-skeleton-card';
      row.appendChild(card);
    }
    content.appendChild(row);
  }

  function hideSkeleton(el) {
    if (!el) return;
    el.classList.remove('hm-jdgm-skeleton');
    var row = el.querySelector('.hm-jdgm-skeleton-row');
    if (row && row.parentNode) row.parentNode.removeChild(row);
  }

  function patchFetch() {
    if (fetchPatched || typeof window.fetch !== 'function') return;
    fetchPatched = true;
    var nativeFetch = window.fetch.bind(window);

    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      if (!url || url.indexOf('reviews_for_carousel') === -1) {
        return nativeFetch(input, init);
      }

      log('reviews_for_carousel fetch at T=' + nowMs() + 'ms', url.slice(0, 120));

      return nativeFetch(input, init).then(function (response) {
        var cloned = response.clone();
        return cloned
          .json()
          .then(function (data) {
            var carouselEl = document.querySelector('.jdgm-cards-carousel');
            var width = getRetinaImageWidth(carouselEl);
            log('rewriting review image URLs to width=' + width + ' (2× CSS card width)');
            var rewritten = rewriteDeep(data, width);
            return new Response(JSON.stringify(rewritten), {
              status: response.status,
              statusText: response.statusText,
              headers: response.headers
            });
          })
          .catch(function () {
            return response;
          });
      });
    };
  }

  function rewriteImgNode(img, width) {
    if (!img || img.nodeName !== 'IMG') return;
    var src = img.getAttribute('src') || '';
    if (!src || src.indexOf('width=') === -1) return;
    if (src.indexOf('review-images.judgeme.com') === -1) return;
    var next = rewriteImageUrl(src, width);
    if (next !== src) {
      img.setAttribute('src', next);
      if (img.srcset) img.removeAttribute('srcset');
    }
  }

  function observeImages(carouselEl) {
    if (!carouselEl || carouselEl.getAttribute('data-hm-img-obs') === '1') return;
    carouselEl.setAttribute('data-hm-img-obs', '1');
    var width = getRetinaImageWidth(carouselEl);
    var obs = new MutationObserver(function (mutations) {
      width = getRetinaImageWidth(carouselEl);
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'attributes' && m.target && m.target.nodeName === 'IMG') {
          rewriteImgNode(m.target, width);
        }
        var nodes = m.addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var n = nodes[j];
          if (n.nodeType !== 1) continue;
          if (n.nodeName === 'IMG') rewriteImgNode(n, width);
          if (n.querySelectorAll) {
            var imgs = n.querySelectorAll('img');
            for (var k = 0; k < imgs.length; k++) rewriteImgNode(imgs[k], width);
          }
        }
      }
    });
    obs.observe(carouselEl, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset']
    });
  }

  function scheduleUnpark(blockId, el, runInit) {
    if (deferred[blockId]) return;
    deferred[blockId] = true;

    // Prefer section wrapper — carousel is display:none via .jdgm-hidden until
    // reviews load, which would prevent IntersectionObserver from ever firing.
    var observeTarget = el.closest('.shopify-section') || el.closest('.page-width') || el;

    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          io.disconnect();
          log('near viewport — running deferred initCarousel for', blockId, 'at T=' + nowMs() + 'ms');
          hideSkeleton(el);
          el.removeAttribute('data-hm-jdgm-parked');
          observeImages(el);
          try {
            runInit();
          } catch (err) {
            warn('deferred init failed', err);
          }
          break;
        }
      },
      { root: null, rootMargin: ROOT_MARGIN_PX + 'px 0px', threshold: 0 }
    );

    io.observe(observeTarget);
    log(
      'IntersectionObserver armed for',
      blockId,
      'target=' + (observeTarget.id || observeTarget.className),
      'rootMargin=' + ROOT_MARGIN_PX + 'px'
    );
  }

  function shouldDeferCards(settings, el) {
    if (DESIGN_MODE) return false;
    if (settings && settings.carousel_type && settings.carousel_type !== 'cards') return false;
    if (el && !el.classList.contains('jdgm-cards-carousel')) {
      // Still allow if settings say cards but class missing momentarily
      if (!(settings && settings.carousel_type === 'cards')) return false;
    }
    if (!el) return false;
    return !isNearViewport(el, ROOT_MARGIN_PX);
  }

  function wrapInitCarousel(utils) {
    if (!utils || typeof utils.initCarousel !== 'function') return false;
    if (utils._hmWrapped) return true;

    var original = utils.initCarousel.bind(utils);
    utils.initCarousel = function (blockId, settings, averageRating, params, buildFunction) {
      var el = findCarouselEl(blockId);
      var callAt = nowMs();
      var raceOk = wrapperInstalledAt > 0 && callAt >= wrapperInstalledAt;
      var rect = el ? el.getBoundingClientRect() : null;
      var near = el ? isNearViewport(el, ROOT_MARGIN_PX) : null;

      log('initCarousel called', {
        blockId: blockId,
        carousel_type: settings && settings.carousel_type,
        T: callAt + 'ms',
        wrapperInstalledAt: wrapperInstalledAt + 'ms',
        raceOk_wrapperBeforeCall: raceOk,
        nearViewport: near,
        rect: rect
          ? {
              w: Math.round(rect.width),
              h: Math.round(rect.height),
              top: Math.round(rect.top),
              absTop: Math.round(rect.top + (window.scrollY || 0))
            }
          : null,
        offsetTopWalk: el ? getDocumentOffsetTop(el) : null,
        hasCardsClass: !!(el && el.classList.contains('jdgm-cards-carousel')),
        hasMediaCardsAlready: !!(el && el.querySelector('.jdgm-media-card'))
      });

      if (!raceOk) {
        warn('RACE: initCarousel ran before wrapper was installed — deferral may have missed');
      }

      if (shouldDeferCards(settings, el)) {
        log('DEFERRED until near viewport', blockId);
        showSkeleton(el);
        el.setAttribute('data-hm-jdgm-parked', '1');
        scheduleUnpark(blockId, el, function () {
          el.removeAttribute('data-hm-jdgm-parked');
          original(blockId, settings, averageRating, params, buildFunction);
        });
        return;
      }

      log('running initCarousel immediately', blockId, near ? '(near viewport)' : '(no defer)');
      if (el) observeImages(el);
      return original(blockId, settings, averageRating, params, buildFunction);
    };

    utils._hmWrapped = true;
    wrapperInstalledAt = nowMs();
    log('initCarousel wrapper installed at T=' + wrapperInstalledAt + 'ms');
    return true;
  }

  function installUtilsTrap() {
    var existing = window.jdgmCarouselUtils;
    if (existing && typeof existing === 'object') {
      wrapInitCarousel(existing);
      return;
    }

    var held;
    try {
      Object.defineProperty(window, 'jdgmCarouselUtils', {
        configurable: true,
        enumerable: true,
        get: function () {
          return held;
        },
        set: function (val) {
          held = val;
          wrapInitCarousel(val);
        }
      });
      log('jdgmCarouselUtils property trap installed at T=' + nowMs() + 'ms');
    } catch (err) {
      warn('could not trap jdgmCarouselUtils, falling back to poll', err);
      var tries = 0;
      var timer = setInterval(function () {
        tries += 1;
        if (wrapInitCarousel(window.jdgmCarouselUtils) || tries > 200) clearInterval(timer);
      }, 25);
    }
  }

  patchFetch();
  installUtilsTrap();
})();
